import React, { useState, useEffect } from 'react';
import { Typography, Form, Button, Radio, message, Select, Alert, Spin } from 'antd';
import { CloudSun, ArrowLeft, Save, CheckCircle, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SimpleDashboardLayout from '../components/layout/SimpleDashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { deviceService, IDevice } from '../services/deviceService';
import { triggerWeatherBroadcast } from '../services/weatherBroadcastService';

const { Title, Text } = Typography;

// Mock data for districts and villages
const districts = [
  { value: 'ahmednagar', label: 'Ahmednagar' },
  { value: 'pune', label: 'Pune' },
  { value: 'nashik', label: 'Nashik' },
  { value: 'solapur', label: 'Solapur' },
  { value: 'satara', label: 'Satara' },
];

const villagesByDistrict: Record<string, Array<{ value: string; label: string }>> = {
  ahmednagar: [
    { value: 'akole', label: 'Akole' },
    { value: 'jamkhed', label: 'Jamkhed' },
    { value: 'karjat', label: 'Karjat' },
    { value: 'shrigonda', label: 'Shrigonda' },
  ],
  pune: [
    { value: 'ambegaon', label: 'Ambegaon' },
    { value: 'baramati', label: 'Baramati' },
    { value: 'bhor', label: 'Bhor' },
    { value: 'daund', label: 'Daund' },
  ],
  nashik: [
    { value: 'baglan', label: 'Baglan' },
    { value: 'chandvad', label: 'Chandvad' },
    { value: 'deola', label: 'Deola' },
    { value: 'dindori', label: 'Dindori' },
  ],
  solapur: [
    { value: 'akkalkot', label: 'Akkalkot' },
    { value: 'barshi', label: 'Barshi' },
    { value: 'karmala', label: 'Karmala' },
    { value: 'madha', label: 'Madha' },
  ],
  satara: [
    { value: 'jaoli', label: 'Jaoli' },
    { value: 'karad', label: 'Karad' },
    { value: 'khatav', label: 'Khatav' },
    { value: 'koregaon', label: 'Koregaon' },
  ],
};

const WeatherStationForm: React.FC = () => {
  const [form] = Form.useForm();
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [existingDevice, setExistingDevice] = useState<IDevice | null>(null);

  // Broadcast trigger state
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [broadcastStatus, setBroadcastStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { t, language } = useLanguage();

  useEffect(() => {
    fetchDevice();
  }, []);

  const fetchDevice = async () => {
    try {
      const devices = await deviceService.list();
      const wsDevice = devices.find(d => d.type === 'weather_station');
      if (wsDevice) {
        setExistingDevice(wsDevice);
        setSelectedDistrict(wsDevice.district || '');
        form.setFieldsValue({
          district: wsDevice.district,
          village: wsDevice.village,
          frequency: wsDevice.frequency,
          language: wsDevice.preferredLanguage,
        });
      }
    } catch (err) {
      console.error('Failed to fetch devices', err);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleDistrictChange = (value: string) => {
    setSelectedDistrict(value);
    form.setFieldsValue({ village: undefined });
  };

  const handleWeatherBroadcast = async () => {
    setBroadcastLoading(true);
    setBroadcastStatus(null);
    try {
      const result = await triggerWeatherBroadcast();
      setBroadcastStatus({ type: 'success', text: result.message });
      message.success('WhatsApp update queued successfully!');
    } catch (err: any) {
      setBroadcastStatus({ type: 'error', text: err?.message || 'Failed to trigger update' });
      message.error('Failed to queue WhatsApp update');
    } finally {
      setBroadcastLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    setSuccess(false);
    try {
      if (existingDevice) {
        // Edit mode
        const updated = await deviceService.update(existingDevice._id, {
          district: values.district,
          village: values.village,
          frequency: values.frequency,
          preferredLanguage: values.language,
        });
        setExistingDevice(updated);
        message.success('Weather station settings updated!');
      } else {
        // Create mode
        const created = await deviceService.create({
          type: 'weather_station',
          district: values.district,
          village: values.village,
          frequency: values.frequency,
          preferredLanguage: values.language,
        });
        setExistingDevice(created);
        message.success('Weather updates subscription created successfully!');
      }
      setSuccess(true);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to save settings. Please try again.';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <SimpleDashboardLayout>
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      </SimpleDashboardLayout>
    );
  }

  const isEditMode = !!existingDevice;

  return (
    <SimpleDashboardLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center mb-6">
          <Link to="/dashboard" className="mr-4">
            <Button
              type="text"
              icon={<ArrowLeft className="w-4 h-4" />}
              aria-label={t('back')}
            />
          </Link>
          <div>
            <Title level={4} className="mb-0 flex items-center">
              <CloudSun className="w-6 h-6 mr-2 text-blue-500" />
              {isEditMode ? 'Weather Station Settings' : t('weather_station')}
            </Title>
            <Text type="secondary">
              {isEditMode ? 'Manage your weather subscription' : t('get_weather_updates')}
            </Text>
          </div>
        </div>

        {success && (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircle className="w-4 h-4" />}
            message={isEditMode ? "Settings Updated!" : "Subscription saved!"}
            description={
              isEditMode
                ? "Your weather notification preferences have been saved."
                : "You will receive weather updates for your selected location on WhatsApp."
            }
            className="mb-4"
            closable
            onClose={() => setSuccess(false)}
          />
        )}

        <div
          className="p-4 rounded-lg shadow-sm border mb-6"
          style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
          >
            <Form.Item
              name="district"
              label={t('select_district')}
              rules={[{ required: true, message: t('select_district') }]}
            >
              <Select
                placeholder={t('select_district')}
                onChange={handleDistrictChange}
                size="large"
                options={districts}
              />
            </Form.Item>

            <Form.Item
              name="village"
              label={t('select_village')}
              rules={[{ required: true, message: t('select_village') }]}
            >
              <Select
                placeholder={selectedDistrict ? t('select_village') : t('select_district_first')}
                disabled={!selectedDistrict}
                size="large"
                options={selectedDistrict ? villagesByDistrict[selectedDistrict] : []}
              />
            </Form.Item>

            <Form.Item
              name="frequency"
              label={t('update_frequency')}
              initialValue="daily"
              rules={[{ required: true, message: t('update_frequency') }]}
            >
              <Radio.Group size="large">
                <Radio.Button value="hourly">{t('hourly') || 'Hourly'}</Radio.Button>
                <Radio.Button value="daily">{t('daily')}</Radio.Button>
                <Radio.Button value="weekly">{t('weekly')}</Radio.Button>
                <Radio.Button value="alerts">{t('alerts_only')}</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="language"
              label={t('preferred_language')}
              initialValue={language}
              rules={[{ required: true, message: t('preferred_language') }]}
            >
              <Radio.Group size="large">
                <Radio.Button value="marathi">मराठी</Radio.Button>
                <Radio.Button value="hindi">हिंदी</Radio.Button>
                <Radio.Button value="english">English</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item className="mt-8 mb-0">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<Save className="w-4 h-4" />}
                size="large"
                block
              >
                {isEditMode ? 'Save Setup' : t('subscribe_weather')}
              </Button>
            </Form.Item>
          </Form>
        </div>

        {isEditMode && (
          <div
            className="p-4 rounded-lg shadow-sm border mb-6"
            style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="w-5 h-5 text-green-500" />
              <Title level={5} className="mb-0" style={{ color: 'var(--color-text)' }}>
                Manual Weather Update
              </Title>
            </div>
            <Text type="secondary" className="block mb-4">
              Get the latest weather alert sent directly to your WhatsApp number right now.
            </Text>

            {broadcastStatus && (
              <Alert
                type={broadcastStatus.type}
                message={broadcastStatus.text}
                className="mb-3"
                showIcon
                closable
                onClose={() => setBroadcastStatus(null)}
              />
            )}

            <Button
              type="primary"
              icon={<MessageCircle className="w-4 h-4" />}
              loading={broadcastLoading}
              onClick={handleWeatherBroadcast}
              className="w-full"
              style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
            >
              {broadcastLoading ? 'Sending...' : 'Request Update on WhatsApp'}
            </Button>
          </div>
        )}

        <div className="mt-6 p-4 rounded-lg border border-blue-300 dark:border-blue-800" style={{ background: 'var(--color-primary-subtle)' }}>
          <Text style={{ color: 'var(--color-text)' }}>
            <strong>{t('note')}:</strong> {t('weather_note')}
          </Text>
        </div>
      </motion.div>
    </SimpleDashboardLayout>
  );
};

export default WeatherStationForm;
